import React, { useEffect, useMemo, useState } from 'react'
import type { PresentationOptionV2, PresentationPreview, QuoteDocument } from 'paintscout'
import type { QuoteView } from '../../Quote'
import { Footer } from '../components'
import { getPresentationTheme } from '../util/getPresentationTheme'
import PresentationNavContextProvider from '../PresentationNav/PresentationNavContext/PresentationNavContextProvider'
import PresentationNavMobile from '../PresentationNav/PresentationNavMobile/PresentationNavMobile'
import Presentation from '../Presentation/Presentation'
import { Formik } from 'formik'
import { useFrame } from 'react-frame-component'
import { PresentationContext, usePresentation, useQuote } from '../../context'
import classnames from 'classnames'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { PRESENTATION_DEVICE_WIDTH } from '../util'
import { ExitPreviewButton } from '../../common'

const useStyles = makeStyles<Theme, CustomerViewContentsProps>(
  (theme: Theme) => {
    return {
      // masks the shared `body` background (MuiCssBaseline's `@global body`, always the app's grey
      // default) behind/around the centered `layout` column below — v2-only, so v1 CV routes keep
      // the shared grey untouched
      root: {
        backgroundColor: '#fff',
        minHeight: '100vh',
        width: '100%'
      },
      layout: (props) => {
        // height of the mobile navbar, with or without the banner
        // preview/accept adds no banner of its own any more — it exits via the floating
        // ExitPreviewButton in the corner (ENG-1505), which takes no space in the nav.
        const hasBanner = !!props.banner
        const topMargin = props.hideNav ? '0' : hasBanner ? '171px' : '117px'
        const topMarginMobile = props.hideNav ? '0' : hasBanner ? '134px' : '78px'

        return {
          overflow: 'hidden',
          position: 'relative',
          display: 'block',
          margin: `${topMargin} auto 0 auto`,
          // matches PRESENTATION_DEVICE_WIDTH.desktop — the admin's "Desktop" preview renders this
          // in an iframe at that exact width, so a smaller maxWidth here left a visible sliver of
          // extra whitespace outside this column that isn't part of any block's own padding
          maxWidth: PRESENTATION_DEVICE_WIDTH.desktop,
          padding: theme.spacing(0, 4),
          [theme.breakpoints.down('md')]: {
            padding: 0
          },
          [theme.breakpoints.down('xs')]: {
            margin: `${topMarginMobile} auto 0 auto`
          },
          '@media print': {
            paddingTop: 0,
            marginTop: 0
          }
        }
      },
      invoice: {
        marginTop: 0
      },
      presentationWrapper: {
        '@media print': {
          width: 'auto',
          padding: 0
        }
      }
    }
  },
  { name: 'CustomerViewContents' }
)

export interface CustomerViewContentsProps {
  /** rendered inside the layout (which owns the fixed-nav top margin) before the presentation, e.g. quote alerts/payment requests */
  aboveContent?: React.ReactNode
  allowResponse?: boolean
  banner?: React.ReactNode
  ChatButton?: React.ReactNode
  classes?: StyleClasses<typeof useStyles>
  currentPage: string
  getHeight?: (height: number) => number
  handleAcceptClick?: (_event: any) => void
  handleMoreClick?: (_event: any, data: string) => void
  handlePageClick: (ev: React.MouseEvent, pageKey: string) => void
  handlePayClick?: (_event: any, amount: number) => void
  handleSectionClick: (ev: React.MouseEvent, pageKey: string) => void
  hideFooter?: boolean
  hideNav?: boolean
  /** Skips the accepted/paid/invoiced status badge — for previews that use the placeholder demo
   * quote (which hardcodes status 'accepted') rather than a real one, e.g. a section-template
   * thumbnail gallery (#22683). */
  hideAcceptedBadge?: boolean
  isInvoice?: boolean
  isPreview?: boolean
  isPreviewAccept?: boolean
  isScreenshot?: boolean
  loadAllFonts?: boolean
  navOpen: boolean
  onDismissDialog?: (event: any, reason: 'backdropClick' | 'escapeKeyDown') => void
  presentation: PresentationOptionV2
  preview?: PresentationPreview
  responding?: boolean
  setNavOpen: (open: boolean) => void
  showChat?: QuoteDocument['owner']
  usePresentationFromProp?: boolean
  view: QuoteView
}

function CustomerViewContents(props: CustomerViewContentsProps) {
  const classes = useStyles(props)
  const {
    aboveContent,
    allowResponse = true,
    banner,
    ChatButton,
    currentPage,
    getHeight,
    handleAcceptClick,
    handlePayClick,
    handleMoreClick,
    handlePageClick,
    handleSectionClick,
    hideFooter,
    hideNav,
    hideAcceptedBadge,
    isInvoice,
    isPreview,
    isPreviewAccept,
    isScreenshot,
    loadAllFonts,
    navOpen,
    onDismissDialog,
    presentation,
    preview,
    responding,
    setNavOpen,
    showChat,
    usePresentationFromProp,
    view
  } = props
  const [cartOpen, setCartOpen] = useState(false)

  const style = presentation.advanced?.style || {}
  const presentationTheme = useMemo(() => getPresentationTheme(style), [style])
  const { quote } = useQuote()
  const { document: doc } = useFrame()

  // This is the customer-facing render — never an editing surface. It matters because
  // PresentationImbedPreview mounts this inside an iframe via a React portal, and context crosses
  // portals: without this override the *editor's* `isEditable: true` leaks into the preview, and
  // fields would show unresolved `{{tag}}` chips instead of real content. Everything else in the
  // context is passed through untouched.
  const presentationContext = usePresentation()
  const customerViewContext = useMemo(() => ({ ...presentationContext, isEditable: false }), [presentationContext])

  const isQuoteEmpty = useMemo(() => {
    return Object.keys(quote?.lineItems || {}).length === 0 && !quote.importedContent && !quote.is_invoice
  }, [quote.lineItems, quote.importedContent, quote.is_invoice])

  // iOS Safari's overscroll bounce reveals whatever is behind `body`, not `root` — `root`'s white
  // background doesn't extend past the page bounds during rubber-banding, so `html`'s own default
  // grey shows through at the top on bounce-back. Force it white while this mounts, restore after.
  useEffect(() => {
    const htmlEl = doc?.documentElement
    if (!htmlEl) {
      return
    }
    const previousBackgroundColor = htmlEl.style.backgroundColor
    htmlEl.style.backgroundColor = '#fff'
    return () => {
      htmlEl.style.backgroundColor = previousBackgroundColor
    }
  }, [doc])

  return (
    <div className={classes.root}>
      <PresentationContext.Provider value={customerViewContext}>
        <PresentationNavContextProvider
          allowResponse={allowResponse}
          cartOpen={cartOpen}
          currentPage={currentPage}
          currentSection={currentPage}
          isCustomerView
          isPreview={isPreview}
          isPreviewAccept={isPreviewAccept}
          isQuoteEmpty={isQuoteEmpty}
          isResponding={responding}
          onAcceptClick={handleAcceptClick}
          onClose={() => setNavOpen(false)}
          onCloseCart={() => setCartOpen(false)}
          onDismissDialog={onDismissDialog}
          onMoreClick={handleMoreClick}
          onOpen={() => setNavOpen(true)}
          onOpenCart={() => setCartOpen(true)}
          onPageClick={handlePageClick}
          onPayClick={handlePayClick}
          onSectionClick={handleSectionClick}
          open={navOpen}
          presentation={presentation}
          preview={preview}
          view={view}
        >
          <div className={classnames({ [classes.layout]: true, [classes.invoice]: isInvoice })}>
            {!isScreenshot && !hideNav && <PresentationNavMobile banner={banner} classes={classes} />}
            <div className={classes.presentationWrapper}>
              {aboveContent}
              <Formik
                initialValues={presentation}
                onSubmit={() => {
                  // Do nothing, formik here for context
                }}
              >
                <Presentation
                  isPreview={isPreview}
                  isScreenshot={isScreenshot}
                  isSettings={false}
                  hideAcceptedBadge={hideAcceptedBadge}
                  loadAllFonts={loadAllFonts}
                  getHeight={getHeight}
                  /*
                   * `currentPage` is whatever the nav last emitted, and that's a page key when
                   * multi-page and a section key when single-page (#21990) — Presentation resolves
                   * either to its owning page, and falls back to the first page. It must NOT be
                   * nulled out for "stacked": a v2 page always renders alone with its sections
                   * stacked inside it, so passing null pinned the customer view to page 1 and made
                   * every page tab a no-op.
                   */
                  page={currentPage}
                  presentation={isScreenshot || usePresentationFromProp ? presentation : null}
                  presentationTheme={presentationTheme}
                  view={view}
                />
              </Formik>
              {showChat && ChatButton}
              {!isScreenshot && !hideFooter && <Footer />}
            </div>
          </div>
        </PresentationNavContextProvider>
        {/*
         * Preview/accept (the dialog opened from EditableQuote's "Preview" action) exits with the
         * same floating pill v1's preview uses, so the control does not move when a client
         * switches presentation versions (ENG-1505). It replaced a "Back to Estimate" bar that sat
         * above the nav at the top of the page.
         */}
        {isPreviewAccept && !isScreenshot && (
          <ExitPreviewButton onClick={(ev) => onDismissDialog?.(ev, 'escapeKeyDown')} />
        )}
      </PresentationContext.Provider>
    </div>
  )
}

export default CustomerViewContents
